// DebugOverlay — on-screen diagnostics for a TV with no devtools (?debug=1).
//
// Shows what the TV's browser reports about itself, the viewport and every
// <video> on the page, refreshed each second, so a photo of the screen tells
// us the engine and whether video frames are actually being decoded.
import React, { useEffect, useState } from "react";
import { adsDucked } from "../utils/adAudio";
import { adSoundDisabled } from "../utils/debugFlags";

const tail = (s: string) => (s.length > 48 ? "…" + s.slice(-48) : s);

const videoLine = (v: HTMLVideoElement, i: number): string => {
  const q = (v as any).getVideoPlaybackQuality?.();
  const frames =
    q?.totalVideoFrames ?? (v as any).webkitDecodedFrameCount ?? "?";
  const dropped = q?.droppedVideoFrames ?? "?";
  return (
    `video${i}: ${tail(v.currentSrc || v.src)}\n` +
    `  paused=${v.paused} muted=${v.muted} vol=${v.volume} ended=${v.ended}\n` +
    `  ready=${v.readyState} net=${v.networkState} err=${v.error?.code ?? "-"}\n` +
    `  size=${v.videoWidth}x${v.videoHeight} t=${v.currentTime.toFixed(1)}/${
      isFinite(v.duration) ? v.duration.toFixed(1) : "?"
    } frames=${frames} dropped=${dropped}\n` +
    `  box=${Math.round(v.getBoundingClientRect().width)}x${Math.round(
      v.getBoundingClientRect().height
    )} connected=${v.isConnected}`
  );
};

const snapshot = (): string => {
  const root = document.documentElement;
  const lines = [
    `ua: ${navigator.userAgent}`,
    `viewport: ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio} ` +
      `root=${getComputedStyle(root).fontSize} vis=${document.visibilityState}`,
    `ads: ducked=${adsDucked()} soundDisabled=${adSoundDisabled()} ` +
      `time=${new Date().toLocaleTimeString()}`,
  ];
  document.querySelectorAll("video").forEach((v, i) => lines.push(videoLine(v, i)));
  if (!document.querySelector("video")) lines.push("video: none on page");
  return lines.join("\n");
};

const DebugOverlay: React.FC = () => {
  const [text, setText] = useState(snapshot);
  useEffect(() => {
    const id = setInterval(() => setText(snapshot()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <pre
      style={{
        position: "fixed",
        top: "0.5rem",
        left: "0.5rem",
        zIndex: 9999,
        maxWidth: "60vw",
        margin: 0,
        padding: "0.5rem 0.75rem",
        background: "rgba(0,0,0,0.78)",
        color: "#9BFF9B",
        font: "0.75rem/1.35 ui-monospace, Menlo, monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        borderRadius: "0.5rem",
        pointerEvents: "none",
      }}
    >
      {text}
    </pre>
  );
};

export default DebugOverlay;
